/**
 * Search API functions for posts and users.
 * Uses enhanced existing endpoints with Atlas Search when searchString is provided.
 */

import axios from 'axios';
import Post from '@/models/post';
import User from '@/models/user';
import {
  shouldUseOpenBrowseGuestEndpoints,
  shouldSkipGuestAuthenticatedApiCalls,
  type PlatformBrowseMode,
} from '@/constants/platformBrowseMode';
import { debug, error } from '@/utils/logger';

const API_URL = process.env.NEXT_PUBLIC_APP_API_BASE_URL;

// Endpoints (enhanced existing endpoints)
const POSTS_ENDPOINT = `${API_URL}/api/v2/posts/post_types`;
export const PUBLIC_POSTS_SEARCH_ENDPOINT = `${API_URL}/api/v2/public/posts/post_types`;

const USERS_ENDPOINT = `${API_URL}/api/v2/users/featured`;
const USERS_FILTERED_ENDPOINT = `${API_URL}/api/v1/users/filtered`;
const USERS_BY_CONTENT_ENDPOINT = `${API_URL}/api/v2/users/by-content`;
export const PUBLIC_USERS_FILTERED_ENDPOINT = `${API_URL}/api/v2/public/users/filtered`;
export const PUBLIC_USERS_BY_CONTENT_ENDPOINT = `${API_URL}/api/v2/public/users/by-content`;
export const PUBLIC_USERS_FEATURED_ENDPOINT = `${API_URL}/api/v2/public/users/featured`;

export function resolvePostsSearchEndpoint(
  hasAuth: boolean,
  browseMode?: PlatformBrowseMode
): string | null {
  if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, browseMode)) {
    return null;
  }
  return shouldUseOpenBrowseGuestEndpoints(hasAuth, browseMode, false)
    ? PUBLIC_POSTS_SEARCH_ENDPOINT
    : POSTS_ENDPOINT;
}

function resolveUsersFilteredEndpoint(
  hasAuth: boolean,
  browseMode?: PlatformBrowseMode
): string | null {
  if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, browseMode)) {
    return null;
  }
  return shouldUseOpenBrowseGuestEndpoints(hasAuth, browseMode, false)
    ? PUBLIC_USERS_FILTERED_ENDPOINT
    : USERS_FILTERED_ENDPOINT;
}

function resolveUsersByContentEndpoint(
  hasAuth: boolean,
  browseMode?: PlatformBrowseMode
): string | null {
  if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, browseMode)) {
    return null;
  }
  return shouldUseOpenBrowseGuestEndpoints(hasAuth, browseMode, false)
    ? PUBLIC_USERS_BY_CONTENT_ENDPOINT
    : USERS_BY_CONTENT_ENDPOINT;
}

function resolveUsersFeaturedEndpoint(
  hasAuth: boolean,
  browseMode?: PlatformBrowseMode,
  hasSearchString = false
): string | null {
  if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, browseMode)) {
    return null;
  }
  if (shouldUseOpenBrowseGuestEndpoints(hasAuth, browseMode, false)) {
    // Public featured route supports profile_type browse only (no Atlas name search).
    return hasSearchString ? null : PUBLIC_USERS_FEATURED_ENDPOINT;
  }
  return USERS_ENDPOINT;
}

export interface SearchPostsParams {
  searchString: string;
  limit?: number;
  postTypes?: string;
  tags?: string[];
  travelWithTypes?: string[];
  /** When set, backend filters by exact match on city_identifying_names (strict city search). */
  cityIdentifyingName?: string;
  /** When set, backend filters by country name (e.g. "Australia"). Returns all posts with any location in the country. */
  country?: string;
  page?: number;
  /** When false, backend excludes place-note boost (no post_locations $text merge). Defaults to true. Set false for geographic filters (country, cityIdentifyingName) and category filters (travel_style, travel_with). */
  includePlaceNotes?: boolean;
  /** Runtime browse mode from PlatformBrowseModeContext — guest + OPEN uses public post search. */
  browseMode?: PlatformBrowseMode;
}

export interface SearchPostsResponse {
  posts: Post[];
  isSearchMode: boolean;
}

export interface SearchUsersParams {
  searchString: string;
  limit?: number;
  profileType?: string;
  travellerTypes?: string[];
  travelWithTypes?: string[];
  page?: number;
  browseMode?: PlatformBrowseMode;
}

export interface SearchUsersResponse {
  users: User[];
  isSearchMode: boolean;
}

export interface GetFilteredUsersParams {
  profileType: 'creator' | 'advisor' | 'regular' | 'other';
  page?: number;
  limit?: number;
  travellerTypes?: string[];
  travelWithTypes?: string[];
  /** Runtime browse mode — guest + OPEN uses public users/filtered. */
  browseMode?: PlatformBrowseMode;
}

export interface GetFilteredUsersResponse {
  users: User[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

export interface GetUsersByContentParams {
  searchString?: string;
  tags?: string[];
  cityIdentifyingName?: string;
  /** When set, backend filters by country name (e.g. "Australia"). Returns users with content in the country. */
  country?: string;
  postTypes?: string;
  travelWithTypes?: string[];
  profileType?: 'creator' | 'advisor' | 'regular';
  page?: number;
  limit?: number;
  /** Runtime browse mode — guest + OPEN uses public users/by-content. */
  browseMode?: PlatformBrowseMode;
}

export interface GetUsersByContentResponse {
  users: User[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

/**
 * Helper to get auth headers for API calls
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  
  if (typeof window === 'undefined') return headers;
  
  const rawAccessToken = localStorage.getItem('accessToken');
  if (rawAccessToken) {
    const accessToken = rawAccessToken.startsWith('Bearer ')
      ? rawAccessToken.substring(7)
      : rawAccessToken;
    headers['Authorization'] = accessToken;
    
    const rawIdToken = localStorage.getItem('idToken');
    if (rawIdToken) {
      const idToken = rawIdToken.startsWith('Bearer ') 
        ? rawIdToken.substring(7) 
        : rawIdToken;
      headers['x-id-token'] = idToken;
    }
  }
  
  return headers;
}

/**
 * Get users from GET /api/v1/users/filtered (travel-style context on Results).
 * Use when filterContext.type is travel_style or travel_with; supports travellerTypes and travelWithTypes.
 */
export async function getFilteredUsers(params: GetFilteredUsersParams): Promise<GetFilteredUsersResponse> {
  const {
    profileType,
    page = 1,
    limit = 50,
    travellerTypes,
    travelWithTypes,
    browseMode,
  } = params;

  const queryParams = new URLSearchParams({
    profile_type: profileType,
    page: page.toString(),
    limit: limit.toString(),
  });
  if (travellerTypes != null && travellerTypes.length > 0) {
    queryParams.set('travellerTypes', travellerTypes.join(','));
  }
  if (travelWithTypes != null && travelWithTypes.length > 0) {
    queryParams.set('travelWithTypes', travelWithTypes.join(','));
  }

  debug('[getFilteredUsers] Request', {
    profileType,
    page,
    limit,
    travellerTypes: travellerTypes?.length ? travellerTypes : undefined,
    travelWithTypes: travelWithTypes?.length ? travelWithTypes : undefined,
  });

  try {
    const headers = getAuthHeaders();
    const hasAuth = Object.keys(headers).length > 0;
    const endpoint = resolveUsersFilteredEndpoint(hasAuth, browseMode);
    if (!endpoint) {
      return { users: [], page, limit, total: 0, hasMore: false };
    }
    const response = await axios.get(
      `${endpoint}?${queryParams}`,
      {
        headers: hasAuth ? headers : undefined,
        timeout: 20000,
      }
    );
    const data = response.data;

    const rawUsers = Array.isArray(data?.users) ? data.users : [];
    const users = rawUsers.map((item: Record<string, unknown>) => new User(item));

    debug('[getFilteredUsers] Completed', { resultCount: users.length, total: data.total, hasMore: data.hasMore });

    return {
      users,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      total: data.total ?? 0,
      hasMore: data.hasMore ?? false,
    };
  } catch (err) {
    error('[getFilteredUsers] Error fetching filtered users', err);
    throw err;
  }
}

/**
 * Get users whose posts match content filters (GET /api/v2/users/by-content).
 * Use for "find creators/advisors who have content about X" (destination, tag, city, country).
 * Requires at least one of searchString, tags, cityIdentifyingName, or country.
 */
export async function getUsersByContent(params: GetUsersByContentParams): Promise<GetUsersByContentResponse> {
  const {
    searchString,
    tags,
    cityIdentifyingName,
    country,
    postTypes,
    travelWithTypes,
    profileType,
    page = 1,
    limit = 20,
    browseMode,
  } = params;

  const hasSearch = (searchString ?? '').trim().length > 0;
  const hasTags = tags != null && tags.length > 0;
  const hasCity = (cityIdentifyingName ?? '').trim().length > 0;
  const hasCountry = (country ?? '').trim().length > 0;

  if (!hasSearch && !hasTags && !hasCity && !hasCountry) {
    return { users: [], page: 1, limit: Math.min(limit, 50), total: 0, hasMore: false };
  }

  const queryParams = new URLSearchParams();
  if (hasSearch) {
    queryParams.set('searchString', (searchString ?? '').trim());
  }
  if (hasTags) {
    queryParams.set('tags', tags!.join(','));
  }
  if (hasCountry) {
    queryParams.set('country', (country ?? '').trim());
  }
  if (hasCity && !hasCountry) {
    queryParams.set('cityIdentifyingName', (cityIdentifyingName ?? '').trim());
  }
  if (postTypes) {
    queryParams.set('postTypes', postTypes);
  }
  if (travelWithTypes != null && travelWithTypes.length > 0) {
    queryParams.set('travelWithTypes', travelWithTypes.join(','));
  }
  if (profileType) {
    queryParams.set('profile_type', profileType);
  }
  queryParams.set('page', page.toString());
  queryParams.set('limit', Math.min(limit, 50).toString());

  debug('[getUsersByContent] Request', {
    searchString: hasSearch ? searchString : undefined,
    tags: hasTags ? tags : undefined,
    country: hasCountry ? country : undefined,
    cityIdentifyingName: hasCity && !hasCountry ? cityIdentifyingName : undefined,
    profileType,
    page,
    limit,
  });

  try {
    const headers = getAuthHeaders();
    const hasAuth = Object.keys(headers).length > 0;
    const endpoint = resolveUsersByContentEndpoint(hasAuth, browseMode);
    if (!endpoint) {
      return { users: [], page, limit: Math.min(limit, 50), total: 0, hasMore: false };
    }
    const response = await axios.get(
      `${endpoint}?${queryParams}`,
      {
        headers: hasAuth ? headers : undefined,
        timeout: 20000,
      }
    );
    const data = response.data;

    const rawUsers = Array.isArray(data?.users) ? data.users : [];
    const users = rawUsers.map((item: Record<string, unknown>) => new User(item));

    debug('[getUsersByContent] Completed', { resultCount: users.length, total: data.total, hasMore: data.hasMore });

    return {
      users,
      page: data.page ?? page,
      limit: data.limit ?? limit,
      total: data.total ?? 0,
      hasMore: data.hasMore ?? false,
    };
  } catch (err) {
    error('[getUsersByContent] Error fetching users by content', err);
    throw err;
  }
}

/**
 * Search posts using enhanced /api/v2/posts/post_types endpoint.
 * When searchString is provided, backend uses Atlas Search for better results.
 * Response is always an array.
 */
export async function searchPosts(params: SearchPostsParams): Promise<SearchPostsResponse> {
  const {
    searchString,
    limit = 20,
    postTypes = 'Itinerary,List',
    tags,
    travelWithTypes,
    cityIdentifyingName,
    country,
    page,
    includePlaceNotes,
    browseMode,
  } = params;

  const trimmed = (searchString ?? '').trim();
  const hasSearch = trimmed.length > 0;
  const hasTags = tags != null && tags.length > 0;
  const hasTravelWith = travelWithTypes != null && travelWithTypes.length > 0;
  const hasCity = (cityIdentifyingName ?? '').trim().length > 0;
  const hasCountry = (country ?? '').trim().length > 0;

  if (!hasSearch && !hasTags && !hasTravelWith && !hasCity && !hasCountry) {
    return { posts: [], isSearchMode: false };
  }

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
    postTypes,
    photoFormat: 'identifiers',
  });
  if (hasSearch) {
    queryParams.set('searchString', trimmed);
  }
  if (hasTags) {
    queryParams.set('tags', tags!.join(','));
  }
  if (hasTravelWith) {
    queryParams.set('travelWithTypes', travelWithTypes!.join(','));
  }
  if (hasCountry) {
    queryParams.set('country', (country ?? '').trim());
  }
  if (hasCity && !hasCountry) {
    queryParams.set('cityIdentifyingName', (cityIdentifyingName ?? '').trim());
  }
  if (page) {
    queryParams.set('page', page.toString());
  }
  // Add include_place_notes=false for geographic filters (country or city) and category filters (travel_style, travel_with)
  if (includePlaceNotes === false || (hasCountry || hasCity)) {
    queryParams.set('include_place_notes', 'false');
  }

  debug('[searchPosts] Searching posts', {
    searchString: trimmed || '(category-only)',
    country: hasCountry ? country : undefined,
    cityIdentifyingName: hasCity && !hasCountry ? cityIdentifyingName : undefined,
    limit,
    postTypes,
    tags: hasTags ? tags : undefined,
    travelWithTypes: hasTravelWith ? travelWithTypes : undefined,
    includePlaceNotes: includePlaceNotes !== false && !hasCountry && !hasCity,
  });

  try {
    const headers = getAuthHeaders();
    const hasAuth = Object.keys(headers).length > 0;
    const endpoint = resolvePostsSearchEndpoint(hasAuth, browseMode);
    if (!endpoint) {
      return { posts: [], isSearchMode: false };
    }
    const response = await axios.get(
      `${endpoint}?${queryParams}`,
      {
        headers: hasAuth ? headers : undefined,
        timeout: 20000, // 20s so spinner doesn't hang if backend is slow
      }
    );
    const data = response.data;

    // Backend may return array or { posts: [...] }
    const rawPosts = Array.isArray(data) ? data : (data?.posts ?? []);
    const posts = Array.isArray(rawPosts)
      ? rawPosts.map((item: Record<string, unknown>) => new Post(item))
      : [];

    debug('[searchPosts] Search completed', {
      query: trimmed || 'category-only',
      resultCount: posts.length,
    });

    return {
      posts,
      isSearchMode: true,
    };
  } catch (err) {
    error('[searchPosts] Error searching posts', err);
    throw err;
  }
}

export interface SearchUsersMergedParams {
  searchString: string;
  profileType: 'creator' | 'advisor' | 'regular';
  limit?: number;
  page?: number;
  travelWithTypes?: string[];
  travellerTypes?: string[];
  browseMode?: PlatformBrowseMode;
}

export interface SearchUsersMergedResponse {
  users: User[];
}

/**
 * Merges two user arrays, primary first, deduping by _id. The primary list's ordering is preserved;
 * users only in secondary are appended in their original order.
 */
export function mergeUsersByIdPrimaryFirst(primary: User[], secondary: User[]): User[] {
  const seen = new Set<string>(primary.map((u) => u._id));
  const extras = secondary.filter((u) => !seen.has(u._id));
  return [...primary, ...extras];
}

/**
 * Parallel people search: combines GET /api/v2/users/featured (name-first Atlas Search) with
 * GET /api/v2/users/by-content (content-linked users). Featured results come first; by-content
 * users not already in the featured list are appended. Final list is capped to `limit`.
 *
 * Designed for keyword search contexts where the user may search by display name or by topic.
 * If one request fails, the other's results are still returned.
 */
export async function searchUsersMergedWithByContent(
  params: SearchUsersMergedParams
): Promise<SearchUsersMergedResponse> {
  const {
    searchString,
    profileType,
    limit = 50,
    page = 1,
    travelWithTypes,
    travellerTypes,
    browseMode,
  } = params;

  const headers = getAuthHeaders();
  const hasAuth = Object.keys(headers).length > 0;
  if (shouldSkipGuestAuthenticatedApiCalls(hasAuth, browseMode)) {
    return { users: [] };
  }

  const [featuredResult, byContentResult] = await Promise.allSettled([
    searchUsers({
      searchString,
      profileType,
      limit,
      page,
      travelWithTypes,
      travellerTypes,
      browseMode,
    }),
    getUsersByContent({
      searchString,
      profileType,
      limit,
      page,
      travelWithTypes,
      browseMode,
    }),
  ]);

  const featuredUsers =
    featuredResult.status === 'fulfilled' ? featuredResult.value.users : [];
  const byContentUsers =
    byContentResult.status === 'fulfilled' ? byContentResult.value.users : [];

  if (featuredResult.status === 'rejected') {
    error('[searchUsersMergedWithByContent] Featured request failed', featuredResult.reason);
  }
  if (byContentResult.status === 'rejected') {
    error('[searchUsersMergedWithByContent] By-content request failed', byContentResult.reason);
  }

  const merged = mergeUsersByIdPrimaryFirst(featuredUsers, byContentUsers).slice(0, limit);

  debug('[searchUsersMergedWithByContent] Merged', {
    searchString,
    profileType,
    featuredCount: featuredUsers.length,
    byContentCount: byContentUsers.length,
    mergedCount: merged.length,
  });

  return { users: merged };
}

/**
 * Search users using enhanced /api/v2/users/featured endpoint.
 * When searchString is provided, backend uses Atlas Search for better results.
 * Response is always an array.
 */
export async function searchUsers(params: SearchUsersParams): Promise<SearchUsersResponse> {
  const {
    searchString,
    limit = 20,
    profileType,
    travellerTypes,
    travelWithTypes,
    page,
    browseMode,
  } = params;

  const trimmed = (searchString ?? '').trim();
  const hasSearch = trimmed.length > 0;
  const hasTravellerTypes = travellerTypes != null && travellerTypes.length > 0;
  const hasTravelWith = travelWithTypes != null && travelWithTypes.length > 0;

  if (!hasSearch && !hasTravellerTypes && !hasTravelWith) {
    return { users: [], isSearchMode: false };
  }

  const queryParams = new URLSearchParams({
    limit: limit.toString(),
  });
  if (hasSearch) {
    queryParams.set('searchString', trimmed);
  }
  if (profileType) {
    queryParams.set('profile_type', profileType);
  }
  if (hasTravellerTypes) {
    queryParams.set('travellerTypes', travellerTypes!.join(','));
  }
  if (hasTravelWith) {
    queryParams.set('travelWithTypes', travelWithTypes!.join(','));
  }
  if (page) {
    queryParams.set('page', page.toString());
  }

  debug('[searchUsers] Searching users', {
    searchString: trimmed || '(category-only)',
    limit,
    profileType,
    travellerTypes: hasTravellerTypes ? travellerTypes : undefined,
    travelWithTypes: hasTravelWith ? travelWithTypes : undefined,
  });

  try {
    const headers = getAuthHeaders();
    const hasAuth = Object.keys(headers).length > 0;
    const endpoint = resolveUsersFeaturedEndpoint(hasAuth, browseMode, hasSearch);
    if (!endpoint) {
      return { users: [], isSearchMode: hasSearch };
    }
    const response = await axios.get(
      `${endpoint}?${queryParams}`,
      {
        headers: hasAuth ? headers : undefined,
        timeout: 20000,
      }
    );
    const data = response.data;

    // Response is always an array
    const users = Array.isArray(data)
      ? data.map((item: Record<string, unknown>) => new User(item))
      : [];
    
    debug('[searchUsers] Search completed', {
      query: trimmed || 'category-only',
      resultCount: users.length,
      limit,
      profileType,
    });

    return {
      users,
      isSearchMode: true,
    };
  } catch (err) {
    error('[searchUsers] Error searching users', err);
    throw err;
  }
}
